import React, { useCallback, useEffect, useState } from "react";
import { Bot, Check, Copy, Plus, TriangleAlert } from "lucide-react";

import { apiGet, apiPost, getApiSettings, PUBLIC_BASE_URL } from "../../Api/Client.js";
import { EmptyState, Panel, StatusPill } from "../../Shared/ScreenComponents.jsx";
import { formatDateTime } from "../../Shared/ScreenUtils.jsx";

/* Claude Code (MCP) — each person connects Claude Code to the intranet with their
 * own token. The token acts as them: it can do what their own pages let them do,
 * no more (tasks on their projects, sprints if they run sprints). The server
 * decides all of that; this panel only says it plainly before anyone connects.
 *
 * Independent of GATE on purpose: the AXON keys above can be unreachable while
 * MCP tokens work fine, so this panel loads and fails on its own.
 */

const BASE = "/McpAccessLayer/MyMcpTokens/";

function errorMessage(error, fallback) {
  return error?.payload?.detail || error?.payload?.error || error?.message || fallback;
}

function mcpUrl() {
  const base = (getApiSettings().apiBase || "/api").replace(/\/$/, "");
  return `${PUBLIC_BASE_URL}${base}/McpAccessLayer/mcp/`;
}

function CopyButton({ value, label }) {
  const [state, setState] = useState("");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
      window.setTimeout(() => setState(""), 1600);
    } catch {
      setState("blocked");
    }
  };
  return (
    <button type="button" className="Soft-Button Small" onClick={copy}>
      {state === "copied" ? <Check size={14} /> : <Copy size={14} />}{" "}
      {state === "copied" ? "Copied" : state === "blocked" ? "Select and copy it manually" : label}
    </button>
  );
}

const codeStyle = {
  display: "block",
  padding: "12px 14px",
  borderRadius: 10,
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-subtle)",
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  wordBreak: "break-all",
  userSelect: "all",
};

/* Shown once, right after creating. The command carries the token, so it goes
   away with it. */
function TokenReveal({ token, onDismiss }) {
  const command = `claude mcp add --transport http intranet ${mcpUrl()} --header "Authorization: Bearer ${token}"`;
  return (
    <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
      <p style={{ margin: 0, fontWeight: 600, color: "var(--danger-text)" }}>
        <TriangleAlert size={14} style={{ verticalAlign: "-2px" }} /> This will not be shown again.
      </p>
      <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
        Run this once in a terminal to connect Claude Code. Anyone with this token acts as you, so
        don't paste it into chats, tickets or shared files.
      </p>
      <code style={codeStyle}>{command}</code>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <CopyButton value={command} label="Copy command" />
        <CopyButton value={token} label="Copy token only" />
        <button type="button" className="Primary-Button Small" onClick={onDismiss}>Done</button>
      </div>
    </div>
  );
}

function statusTone(status) {
  if (status === "Active") return "green";
  if (status === "Expired") return "gold";
  return "red";
}

export function McpTokensPanel() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ name: "Claude Code", days: "90" });
  const [minted, setMinted] = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    try {
      setData(await apiGet(BASE));
    } catch (error) {
      setLoadError(errorMessage(error, "Unable to load your MCP tokens."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tokens = data?.tokens || [];
  const active = tokens.filter((token) => token.status === "Active");
  const maxActive = data?.max_active || 5;
  const can = data?.can || {};
  const canAnything = Boolean(can.tasks || can.sprints || can.admin_tools);

  const create = async () => {
    setBusy("create");
    setNotice("");
    try {
      const result = await apiPost(BASE, { name: form.name.trim(), days: Number(form.days) });
      if (result?.token) setMinted(result.token);
      else setNotice("The token was created but none came back. Revoke it and try again.");
    } catch (error) {
      setNotice(errorMessage(error, "Unable to create a token."));
    }
    await load();
    setBusy("");
  };

  const revoke = async (keyId) => {
    setBusy(`revoke-${keyId}`);
    setNotice("");
    try {
      await apiPost(`${BASE}${keyId}/revoke/`, {});
      setConfirmRevoke("");
      setNotice("Token revoked. Claude Code using it stops working straight away.");
    } catch (error) {
      setNotice(errorMessage(error, "Unable to revoke that token."));
    }
    await load();
    setBusy("");
  };

  return (
    <Panel
      title={<span><Bot size={16} style={{ verticalAlign: "-3px" }} /> Claude Code (MCP)</span>}
      subtitle="Work with your tasks and sprints from Claude Code. A token acts as you, with your own access."
    >
      {loadError && <p style={{ margin: "0 0 12px", color: "var(--danger-text)" }}>{loadError}</p>}

      {data && (
        <>
          {minted && <TokenReveal token={minted} onDismiss={() => setMinted("")} />}

          <ul style={{ margin: "0 0 14px", paddingLeft: 18, fontSize: 13, color: "var(--text-muted)" }}>
            <li>
              Tasks:{" "}
              {can.tasks ? "read and change tasks on the projects you're on, as on the Projects page." : "not available — you don't have the Projects page."}
            </li>
            <li>
              Sprints:{" "}
              {can.sprint_changes
                ? "read and run sprints (create, propose, review, complete) as on Project Management."
                : can.sprints
                  ? "read the sprints of your projects."
                  : "not available."}
            </li>
            {can.admin_tools && <li>Admin tools (people, leads, analytics, GitHub issues) are available to you as an administrator.</li>}
            <li>Every call is recorded under your name.</li>
          </ul>

          {notice && <p role="status" style={{ margin: "0 0 12px", fontSize: 13 }}>{notice}</p>}

          {tokens.length ? (
            <div className="Table-Wrap" style={{ marginBottom: 14 }}>
              <table className="Erp-Table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Key</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Last used</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((token) => (
                    <React.Fragment key={token.key_id}>
                      <tr>
                        <td><strong>{token.name}</strong></td>
                        <td><code style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{token.key_id}…</code></td>
                        <td><StatusPill tone={statusTone(token.status)}>{token.status}</StatusPill></td>
                        <td>{formatDateTime(token.created_at) || "—"}</td>
                        <td>{token.expires_at ? formatDateTime(token.expires_at) : "Never"}</td>
                        <td>{token.last_used_at ? formatDateTime(token.last_used_at) : "Never"}</td>
                        <td>
                          {token.status === "Active" && (
                            <button
                              className="Soft-Button Small danger"
                              disabled={Boolean(busy)}
                              onClick={() => setConfirmRevoke(token.key_id)}
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                      {confirmRevoke === token.key_id && (
                        <tr>
                          <td colSpan={7} style={{ background: "var(--danger-soft)" }}>
                            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                              <span style={{ fontSize: 13 }}>
                                Revoke this token? Claude Code using it stops working straight away. This
                                cannot be undone — create a new one to reconnect.
                              </span>
                              <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                                <button className="Soft-Button Small" disabled={Boolean(busy)} onClick={() => setConfirmRevoke("")}>
                                  Keep it
                                </button>
                                <button className="Primary-Button Small" disabled={Boolean(busy)} onClick={() => revoke(token.key_id)}>
                                  {busy === `revoke-${token.key_id}` ? "Revoking…" : "Yes, revoke"}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState label="You haven't connected Claude Code yet." />
          )}

          {active.length >= maxActive ? (
            <p style={{ margin: 0, fontSize: 13, color: "var(--warning-text)" }}>
              You hold {active.length} of {maxActive} active tokens. Revoke one before creating another.
            </p>
          ) : (
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label className="ds-field" style={{ minWidth: 200 }}>
                <span className="ds-label">Name</span>
                <input
                  value={form.name}
                  maxLength={160}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <label className="ds-field">
                <span className="ds-label">Expires after</span>
                <select value={form.days} onChange={(event) => setForm({ ...form, days: event.target.value })}>
                  {(data.lifetimes || [30, 90, 180]).map((days) => (
                    <option key={days} value={String(days)}>{days} days</option>
                  ))}
                </select>
              </label>
              <button className="Primary-Button" disabled={Boolean(busy) || !canAnything} onClick={create}>
                <Plus size={14} /> {busy === "create" ? "Creating…" : "Connect Claude Code"}
              </button>
            </div>
          )}
          {!canAnything && (
            <p style={{ margin: "10px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
              Your access doesn't include any page the MCP tools work with yet, so a token would have
              nothing to do.
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

export default McpTokensPanel;
